import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import { PlayCircle, ExternalLink } from 'lucide-react'
import { buildVideoUrlWithTimestamp, detectVideoPlatform, isValidHttpUrl } from '../utils/videoExplanation'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const Results = () => {
  const { attemptId } = useParams()
  const navigate = useNavigate()
  const { user, signOut, hasPaidAccess } = useAuth()
  
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedExplanations, setExpandedExplanations] = useState({})

  useEffect(() => {
    if (user && attemptId) {
      loadResults()
    }
  }, [user, attemptId])

  const loadResults = async () => {
    try {
      setLoading(true)
      
      // Get session safely
      let accessToken = null
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) {
          console.error('[Results] Session error:', sessionError.message)
        } else if (sessionData?.session?.access_token) {
          accessToken = sessionData.session.access_token
        }
      } catch (sessionErr) {
        console.error('[Results] Session fetch error:', sessionErr.message || String(sessionErr))
      }

      if (!accessToken) {
        throw new Error('Autentifikácia zlyhala. Prosím prihláste sa znova.')
      }

      const response = await fetch(`${BACKEND_URL}/api/attempts/${attemptId}/results`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })

      if (!response.ok) {
        throw new Error('Nepodarilo sa načítať výsledky')
      }

      const data = await response.json()
      setResults(data)

    } catch (err) {
      console.error('Load results error:', err?.message || String(err))
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (err) {
      console.warn('[Results] Logout error (navigating anyway):', err)
      navigate('/')
    }
  }

  const toggleExplanation = (questionId) => {
    setExpandedExplanations(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam výsledky...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Chyba</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
          >
            Späť na dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Consistent Top Navigation */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm transition-all duration-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <nav className="flex items-center gap-4 text-sm overflow-x-auto scrollbar-hide flex-1">
              <a href="/#preco-t9" className="whitespace-nowrap text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 hover:underline underline-offset-4">
                Prečo T9?
              </a>
              <a href="/#ako-funguje" className="whitespace-nowrap text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 hover:underline underline-offset-4">
                Ako to funguje?
              </a>
              <a href="/#co-ponukame" className="whitespace-nowrap text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 hover:underline underline-offset-4">
                Čo ponúkame?
              </a>
              <a href="/pricing" className="whitespace-nowrap text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 hover:underline underline-offset-4">
                Cenník
              </a>
              <a href="/#faq" className="whitespace-nowrap text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 hover:underline underline-offset-4">
                Časté otázky
              </a>
              <a href="/contact" className="whitespace-nowrap text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 hover:underline underline-offset-4">
                Kontakt
              </a>
            </nav>
            
            {user && (
              <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                <div>
                  {hasPaidAccess ? (
                    <span className="px-3 py-1.5 bg-green-100 text-green-800 rounded-lg font-medium text-sm flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Aktívny
                    </span>
                  ) : (
                    <span className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg font-medium text-sm">
                      Základný
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-600 hidden md:inline">{user?.email}</span>
                <button
                  onClick={handleSignOut}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 whitespace-nowrap"
                >
                  Odhlásiť
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Results Summary */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8" data-testid="results-summary">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Výsledky testu</h1>
            
            {/* Score */}
            <div className="mb-6">
              <div className="text-6xl font-bold text-blue-600 mb-2">
                {results?.percentage?.toFixed(0)}%
              </div>
              <p className="text-gray-600">
                {results?.score} / {results?.max_score} bodov
              </p>
            </div>

            {/* Phase 2: Quick stats — correct count + time spent (only if available) */}
            {(results?.total_questions != null || results?.time_spent_seconds != null) && (
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-700 mb-6" data-testid="quick-stats">
                {results?.total_questions != null && (
                  <div data-testid="stat-correct">
                    <span className="font-semibold text-gray-900">{results.correct_count ?? 0}</span>
                    <span className="text-gray-500"> / {results.total_questions}</span>
                    <span className="text-gray-500 ml-1">správnych odpovedí</span>
                  </div>
                )}
                {results?.time_spent_seconds != null && (
                  <div data-testid="stat-time">
                    <span className="font-semibold text-gray-900">
                      {Math.floor(results.time_spent_seconds / 60)} min {results.time_spent_seconds % 60} s
                    </span>
                    <span className="text-gray-500 ml-1">strávených na teste</span>
                  </div>
                )}
              </div>
            )}

            {/* Phase 2: Educational feedback */}
            {results && (() => {
              const pct = results.percentage ?? 0
              const subject = results.subject
              let band, generic
              if (pct >= 90) { band = 'excellent'; generic = 'Výborný výsledok. Väčšinu úloh si zvládol/zvládla veľmi presne.' }
              else if (pct >= 70) { band = 'good'; generic = 'Dobrá práca. Základy máš zvládnuté, oplatí sa ešte precvičiť náročnejšie úlohy.' }
              else if (pct >= 50) { band = 'partial'; generic = 'Niektoré úlohy ti išli dobre, ale v niektorých témach ešte potrebuješ väčšiu istotu.' }
              else { band = 'low'; generic = 'Tento test ukázal, ktoré oblasti si treba zopakovať. Skús sa vrátiť k základným postupom.' }

              const reviewBySubject = {
                matematika: 'Precvičiť si môžeš percentá, zlomky alebo slovné úlohy.',
                slovenčina: 'Precvičiť si môžeš pravopis, význam slov alebo čítanie s porozumením.',
                financnagramotnost: 'Precvičiť si môžeš rozlišovanie potrieb a želaní, platenie kartou alebo plánovanie výdavkov.'
              }
              const strongBySubject = {
                matematika: 'Najlepšie ti išli výpočtové úlohy.',
                slovenčina: 'Dobre si pracoval/pracovala s textom.',
                financnagramotnost: 'Dobre rozumieš základným situáciám s peniazmi.'
              }
              const review = subject ? reviewBySubject[subject] : null
              const strong = subject && pct >= 70 ? strongBySubject[subject] : null

              const tone = {
                excellent: 'bg-green-50 border-green-200 text-green-900',
                good: 'bg-blue-50 border-blue-200 text-blue-900',
                partial: 'bg-amber-50 border-amber-200 text-amber-900',
                low: 'bg-rose-50 border-rose-200 text-rose-900'
              }[band]

              return (
                <div className={`rounded-lg border p-5 text-left ${tone}`} data-testid="educational-feedback">
                  <p className="font-semibold mb-2">{generic}</p>
                  {strong && <p className="text-sm mb-1" data-testid="feedback-strong">{strong}</p>}
                  {pct < 90 && review && <p className="text-sm" data-testid="feedback-review">💡 {review}</p>}
                </div>
              )
            })()}

            {/* Comparison with others (Phase: fair-stable, percentile-based, gated by MIN_ATTEMPTS) */}
            {results?.ranking && (
              <div className="mt-8" data-testid="ranking-section">
                {results.ranking.comparison_available ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-5 text-center" data-testid="ranking-percentile">
                    <div className="text-4xl font-bold text-green-700 mb-1">
                      {results.ranking.percentile}%
                    </div>
                    <p className="text-gray-700 text-sm">
                      Tvoj výsledok je lepší alebo rovnaký ako výsledok{' '}
                      <strong>{results.ranking.percentile} %</strong> žiakov,
                      ktorí tento test riešili.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Aktuálne porovnanie · {results.ranking.total_attempts} žiakov dokončilo tento test
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 text-center" data-testid="ranking-not-enough">
                    <p className="text-gray-700 text-sm">
                      Zatiaľ nemáme dostatok výsledkov na férové porovnanie.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Test dokončilo {results.ranking.total_attempts} žiakov.
                      Porovnanie zobrazíme po dosiahnutí{' '}
                      {results.ranking.min_attempts_required} dokončených pokusov.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              data-testid="back-to-dashboard"
            >
              Späť na testy
            </button>
          </div>
        </div>

        {/* Test-level explanation video (whole-test card).
            Rendered ONLY when the test has a valid http(s) video URL.
            When no URL is configured this block outputs nothing — existing
            tests without a video look exactly as before. */}
        {isValidHttpUrl(results?.video_explanation_url) && (() => {
          const wholeUrl = buildVideoUrlWithTimestamp(results.video_explanation_url, 0)
          if (!wholeUrl) return null
          const platform = detectVideoPlatform(results.video_explanation_url)
          const iconClass = 'w-5 h-5 flex-shrink-0'
          const Icon = platform && platform.platform === 'youtube' ? PlayCircle : ExternalLink
          const title = (results.video_explanation_title && results.video_explanation_title.trim())
            ? results.video_explanation_title.trim()
            : 'Pozrieť celé video s vysvetlením'
          return (
            <div
              className="bg-white rounded-lg shadow p-6 mb-8 border-l-4 border-blue-500"
              data-testid="results-test-video-card"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Chceš si príklady prejsť ešte raz?
              </h2>
              <p className="text-gray-700 mb-3">
                Pre bližšie vysvetlenie príkladov si pozri naše video.
              </p>
              <a
                href={wholeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                data-testid="results-test-video-link"
              >
                <Icon className={iconClass} aria-hidden="true" />
                <span>{title}</span>
              </a>
              <p className="text-xs text-gray-500 mt-1">
                Video sa otvorí v novej karte.
              </p>
            </div>
          )
        })()}

        {/* Detailed Answers */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Detailné odpovede</h2>
        </div>

        <div className="space-y-6">
          {results?.answers?.map((answer, index) => (
            <div
              key={answer.question_id}
              className={`bg-white rounded-lg shadow p-6 border-l-4 ${
                answer.is_correct ? 'border-green-500' : 'border-red-500'
              }`}
              data-testid={`answer-${index + 1}`}
            >
              {/* Question */}
              <div className="mb-4" translate="no">
                <div className="flex justify-between items-start mb-2">
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 text-sm font-semibold rounded-full">
                    Otázka {index + 1}
                  </span>
                  {answer.is_correct ? (
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                      ✓ Správne
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded-full">
                      ✗ Nesprávne
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {answer.question_text}
                </h3>
              </div>

              {/* User Answer */}
              <div className="mb-4" translate="no">
                <p className="text-sm text-gray-600 mb-1">Vaša odpoveď:</p>
                {answer.user_answer ? (
                  <p className="font-medium text-gray-900">
                    {answer.user_answer}
                    {answer.question_type === 'multiple_choice' && answer.options && (() => {
                      const selectedOption = answer.options.find(opt => opt.option_key === answer.user_answer)
                      return selectedOption ? ` – ${selectedOption.option_text}` : ''
                    })()}
                  </p>
                ) : (
                  <p className="font-medium text-orange-600 italic">
                    Nezodpovedané
                  </p>
                )}
              </div>

              {/* Correct Answer */}
              {!answer.is_correct && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-1">Správna odpoveď:</p>
                  <p className="font-medium text-green-700">
                    {answer.correct_answer}
                    {answer.question_type === 'multiple_choice' && answer.options && (() => {
                      const correctOption = answer.options.find(opt => opt.option_key === answer.correct_answer)
                      return correctOption ? ` – ${correctOption.option_text}` : ''
                    })()}
                  </p>
                </div>
              )}

              {/* Hint and Explanation */}
              {answer.explanation && (answer.explanation.hint || answer.explanation.explanation) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  {/* Hint - Always shown if available */}
                  {answer.explanation.hint && (
                    <div className="bg-blue-50 rounded-lg p-4 mb-3" data-testid={`hint-${index + 1}`}>
                      <p className="font-semibold text-blue-900 mb-1 flex items-center gap-2">
                        <span>💡</span>
                        <span>Nápoveda:</span>
                      </p>
                      <p className="text-blue-800">
                        {answer.explanation.hint}
                      </p>
                    </div>
                  )}
                  
                  {/* Explanation - Collapsed by default with toggle */}
                  {answer.explanation.explanation && (
                    <div>
                      <button
                        onClick={() => toggleExplanation(answer.question_id)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mb-2 transition-colors"
                        data-testid={`explanation-toggle-${index + 1}`}
                      >
                        <svg 
                          className={`w-4 h-4 transition-transform ${expandedExplanations[answer.question_id] ? 'rotate-90' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="flex items-center gap-1.5">
                          <span className="text-xl">🧑‍🏫</span>
                          <span>
                            {expandedExplanations[answer.question_id] ? 'Skryť vysvetlenie' : 'Zobraziť vysvetlenie'}
                          </span>
                        </span>
                      </button>
                      
                      {expandedExplanations[answer.question_id] && (
                        <div className="bg-blue-50 rounded-lg p-4" data-testid={`explanation-${index + 1}`}>
                          <p className="font-semibold text-blue-900 mb-1 flex items-center gap-2">
                            <span>📖</span>
                            <span>Vysvetlenie:</span>
                          </p>
                          <p className="text-blue-800">
                            {answer.explanation.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Per-question video link.
                  Rendered ONLY when BOTH conditions hold:
                    - the test has a valid http(s) video URL
                    - this specific question has a timestamp (integer seconds > 0
                      or === 0 for "from start")
                  Missing timestamp => no placeholder, no disabled element. */}
              {(() => {
                const startSec = answer.video_explanation_start_seconds
                const hasTimestamp = Number.isFinite(Number(startSec)) && Number(startSec) >= 0
                if (!hasTimestamp) return null
                if (!isValidHttpUrl(results?.video_explanation_url)) return null
                const url = buildVideoUrlWithTimestamp(results.video_explanation_url, Number(startSec))
                if (!url) return null
                const platform = detectVideoPlatform(results.video_explanation_url)
                const Icon = platform && platform.platform === 'youtube' ? PlayCircle : ExternalLink
                return (
                  <div
                    className="mt-4 pt-4 border-t border-gray-200"
                    data-testid={`question-video-block-${index + 1}`}
                  >
                    <p className="text-sm font-semibold text-gray-800 mb-2">
                      Nerozumieš postupu?
                    </p>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                      data-testid={`question-video-link-${index + 1}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      <span>Pozrieť vysvetlenie tejto úlohy</span>
                    </a>
                    <p className="text-xs text-gray-500 mt-1">
                      Video sa otvorí v novej karte.
                    </p>
                  </div>
                )
              })()}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Results
